import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  TextField,
  Chip,
  Divider,
  CircularProgress,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  alpha,
  useTheme,
} from "@mui/material";
import {
  ArrowLeft,
  Star,
  Archive,
  MailOpen,
  Mail,
  Send,
  Paperclip,
  FileText,
  Download,
  Building2,
  Plus,
  ChevronDown,
  ChevronUp,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  fetchInboxThread,
  updateThread,
  replyToThread,
  replyToThreadWithAttachments,
  fetchInboxStats,
} from "../../../../../redux/actions/inboxAction";
import { enqueueSnackbar } from "notistack";
import { backendClient } from "../../../../../api/backend";

const EmailThread = ({ threadId }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentThread, messages, loading } = useSelector((state) => state.inbox);
  const fileInputRef = useRef(null);

  const [replyOpen, setReplyOpen] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [replyTo, setReplyTo] = useState([]);
  const [replyCc, setReplyCc] = useState([]);
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [expandedMsgs, setExpandedMsgs] = useState(new Set());
  const [createOrgOpen, setCreateOrgOpen] = useState(false);
  const [orgForm, setOrgForm] = useState({ org_name: "", contact_email: "", contact_first_name: "", contact_last_name: "", contact_phone: "" });
  const [creatingOrg, setCreatingOrg] = useState(false);

  useEffect(() => {
    if (threadId) {
      dispatch(fetchInboxThread(threadId));
    }
  }, [dispatch, threadId]);

  // Mark as read on open
  useEffect(() => {
    if (currentThread && !currentThread.is_read) {
      dispatch(updateThread(threadId, { is_read: true }));
      dispatch(fetchInboxStats());
    }
  }, [currentThread, dispatch, threadId]);

  // Pre-fill reply recipients from last inbound message
  useEffect(() => {
    if (messages.length > 0) {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg.direction === "inbound") {
        setReplyTo([lastMsg.from_email]);
        setReplyCc(lastMsg.cc_emails || []);
      } else {
        setReplyTo(lastMsg.to_emails || []);
      }
      // Expand last 2 messages by default
      const ids = new Set();
      messages.slice(-2).forEach((m) => ids.add(m.id));
      setExpandedMsgs(ids);
    }
  }, [messages]);

  const handleBack = () => navigate("/admin/inbox");

  const handleToggleStar = async () => {
    await dispatch(updateThread(threadId, { is_starred: !currentThread.is_starred }));
    dispatch(fetchInboxThread(threadId));
  };

  const handleToggleArchive = async () => {
    await dispatch(updateThread(threadId, { is_archived: !currentThread.is_archived }));
    navigate("/admin/inbox");
  };

  const handlePickFiles = () => {
    fileInputRef.current?.click();
  };

  const handleFilesSelected = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length === 0) return;
    setReplyAttachments((prev) => [...prev, ...picked]);
    // Reset input so picking the same file again still triggers onChange
    e.target.value = "";
  };

  const handleRemoveAttachment = (idx) => {
    setReplyAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleReply = async () => {
    if (!replyBody.trim()) return;
    setSending(true);
    try {
      const subject = `Re: ${currentThread?.subject || ""}`;
      const bodyHtml = replyBody.replace(/\n/g, "<br>");

      if (replyAttachments.length > 0) {
        const formData = new FormData();
        formData.append("to", replyTo.join(","));
        if (replyCc.length > 0) formData.append("cc", replyCc.join(","));
        formData.append("subject", subject);
        formData.append("body_html", bodyHtml);
        replyAttachments.forEach((f) => formData.append("attachments", f));
        await dispatch(replyToThreadWithAttachments(threadId, formData));
      } else {
        await dispatch(replyToThread(threadId, {
          to: replyTo,
          cc: replyCc.length > 0 ? replyCc : undefined,
          subject,
          body_html: bodyHtml,
        }));
      }

      enqueueSnackbar(t("inbox_reply_sent"), { variant: "success" });
      setReplyBody("");
      setReplyAttachments([]);
      setReplyOpen(false);
    } catch (err) {
      enqueueSnackbar(err.message || t("inbox_send_error"), { variant: "error" });
    } finally {
      setSending(false);
    }
  };

  const handleOpenCreateOrg = () => {
    // Prefill from external participants
    const participants = currentThread?.participants || [];
    const external = participants.find((p) => p.email && !p.email.endsWith("@lawis.ai"));
    const name = external?.name || "";
    const email = external?.email || "";
    const parts = name.split(/\s+/);
    const domain = email.includes("@") ? email.split("@")[1] : "";
    const orgName = domain && !["gmail.com", "yahoo.com", "hotmail.com", "outlook.com"].includes(domain)
      ? domain.split(".")[0].charAt(0).toUpperCase() + domain.split(".")[0].slice(1)
      : "";
    setOrgForm({
      org_name: orgName,
      contact_email: email,
      contact_first_name: parts[0] || "",
      contact_last_name: parts.slice(1).join(" ") || "",
      contact_phone: "",
    });
    setCreateOrgOpen(true);
  };

  const handleCreateOrg = async () => {
    if (!orgForm.org_name.trim()) {
      enqueueSnackbar("Nom de l'organisation requis", { variant: "warning" });
      return;
    }
    setCreatingOrg(true);
    try {
      const formData = new FormData();
      formData.append("org_name", orgForm.org_name);
      if (orgForm.contact_email) formData.append("contact_email", orgForm.contact_email);
      if (orgForm.contact_first_name) formData.append("contact_first_name", orgForm.contact_first_name);
      if (orgForm.contact_last_name) formData.append("contact_last_name", orgForm.contact_last_name);
      if (orgForm.contact_phone) formData.append("contact_phone", orgForm.contact_phone);
      await backendClient.linkOrgToThread(threadId, formData);
      enqueueSnackbar("Organisation créée et liée", { variant: "success" });
      setCreateOrgOpen(false);
      dispatch(fetchInboxThread(threadId));
    } catch (err) {
      enqueueSnackbar(err.message || "Erreur", { variant: "error" });
    } finally {
      setCreatingOrg(false);
    }
  };

  const toggleExpand = (msgId) => {
    setExpandedMsgs((prev) => {
      const next = new Set(prev);
      if (next.has(msgId)) next.delete(msgId);
      else next.add(msgId);
      return next;
    });
  };

  const formatDate = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleString("fr-FR", {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  if (loading && !currentThread) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!currentThread) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>{t("inbox_thread_not_found")}</Typography>
        <Button onClick={handleBack} sx={{ mt: 1 }} startIcon={<ArrowLeft size={14} />}>{t("inbox_back")}</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, flexShrink: 0 }}>
        <IconButton size="small" onClick={handleBack} sx={{ border: `1px solid ${theme.palette.divider}` }}>
          <ArrowLeft size={18} />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: "1rem" }} noWrap>
            {currentThread.subject || t("inbox_no_subject")}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            {currentThread.message_count} {t("inbox_messages")}
          </Typography>
        </Box>
        {currentThread.organization_name ? (
          <Chip
            icon={<Building2 size={12} />}
            label={currentThread.organization_name}
            size="small"
            onClick={() => navigate(`/admin/marketing/organisations/${currentThread.organization_id}`)}
            sx={{
              height: 24, fontSize: "0.7rem", borderRadius: "6px", cursor: "pointer",
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
              "& .MuiChip-icon": { color: theme.palette.primary.main },
            }}
          />
        ) : (
          <Chip
            icon={<Plus size={12} />}
            label="Créer organisation"
            size="small"
            onClick={handleOpenCreateOrg}
            sx={{
              height: 24, fontSize: "0.7rem", borderRadius: "6px", cursor: "pointer",
              border: `1px dashed ${theme.palette.divider}`,
              bgcolor: "transparent",
              color: theme.palette.text.secondary,
              "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.05), borderColor: theme.palette.primary.main, color: theme.palette.primary.main },
              "& .MuiChip-icon": { color: "inherit" },
            }}
          />
        )}
        <IconButton size="small" onClick={handleToggleStar} sx={{ color: currentThread.is_starred ? "#f59e0b" : theme.palette.text.disabled }}>
          <Star size={16} fill={currentThread.is_starred ? "#f59e0b" : "none"} />
        </IconButton>
        <IconButton size="small" onClick={handleToggleArchive} sx={{ color: theme.palette.text.secondary }}>
          <Archive size={16} />
        </IconButton>
      </Box>

      {/* Messages */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", display: "flex", flexDirection: "column", gap: 1.5 }}>
        {messages.map((msg) => {
          const isExpanded = expandedMsgs.has(msg.id);
          const isOutbound = msg.direction === "outbound";
          return (
            <Paper
              key={msg.id}
              elevation={0}
              sx={{
                border: `1px solid ${theme.palette.divider}`, borderRadius: "12px",
                borderLeft: isOutbound ? `3px solid ${theme.palette.primary.main}` : `3px solid ${alpha(theme.palette.text.secondary, 0.3)}`,
                flexShrink: 0,
              }}
            >
              {/* Message header — always visible */}
              <Box
                onClick={() => toggleExpand(msg.id)}
                sx={{
                  display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.25,
                  cursor: "pointer",
                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.02) },
                }}
              >
                {/* Avatar */}
                <Box
                  sx={{
                    width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                    bgcolor: isOutbound ? alpha(theme.palette.primary.main, 0.1) : alpha("#6b7280", 0.1),
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "0.75rem", fontWeight: 700,
                    color: isOutbound ? theme.palette.primary.main : "#6b7280",
                  }}
                >
                  {(msg.from_name || msg.from_email || "?")[0].toUpperCase()}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.8rem" }} noWrap>
                    {msg.from_name || msg.from_email}
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: "0.65rem" }} noWrap>
                    To: {(msg.to_emails || []).join(", ")}
                    {msg.cc_emails?.length > 0 && ` | CC: ${msg.cc_emails.join(", ")}`}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: "0.65rem", flexShrink: 0 }}>
                  {formatDate(msg.gmail_internal_date)}
                </Typography>
                {isExpanded ? <ChevronUp size={14} color={theme.palette.text.disabled} /> : <ChevronDown size={14} color={theme.palette.text.disabled} />}
              </Box>

              {/* Message body — expandable */}
              {isExpanded && (
                <Box sx={{ px: 2, pb: 2 }}>
                  <Divider sx={{ mb: 1.5 }} />
                  {msg.body_html ? (
                    <Box
                      sx={{
                        fontSize: "0.85rem", lineHeight: 1.6,
                        "& img": { maxWidth: "100%" },
                        "& a": { color: theme.palette.primary.main },
                        "& table": { borderCollapse: "collapse" },
                      }}
                      dangerouslySetInnerHTML={{ __html: msg.body_html }}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", fontSize: "0.85rem" }}>
                      {msg.body_text || msg.snippet || ""}
                    </Typography>
                  )}

                  {/* Attachments */}
                  {msg.attachments?.length > 0 && (
                    <Box sx={{ mt: 1.5, display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                      {msg.attachments.map((att, idx) => (
                        <Chip
                          key={idx}
                          icon={<FileText size={12} />}
                          label={`${att.filename} (${formatSize(att.size)})`}
                          size="small"
                          clickable
                          onClick={() => {
                            window.open(
                              `/api/inbox/threads/${threadId}/messages/${msg.id}/attachments/${att.gmail_attachment_id}`,
                              "_blank"
                            );
                          }}
                          deleteIcon={<Download size={12} />}
                          onDelete={() => {}}
                          sx={{ height: 24, fontSize: "0.65rem", borderRadius: "6px" }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Paper>
          );
        })}

        {/* Reply box */}
        <Paper
          elevation={0}
          sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: "12px", p: 2, flexShrink: 0 }}
        >
          {!replyOpen ? (
            <Button
              fullWidth
              variant="text"
              onClick={() => setReplyOpen(true)}
              sx={{ textTransform: "none", fontSize: "0.85rem", color: theme.palette.text.secondary }}
            >
              {t("inbox_reply")}
            </Button>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="caption" sx={{ fontSize: "0.7rem", color: theme.palette.text.secondary }}>
                To: {replyTo.join(", ")}
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={4}
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                placeholder={t("inbox_reply_placeholder")}
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.85rem" } }}
              />

              {/* Hidden file input */}
              <input
                type="file"
                multiple
                ref={fileInputRef}
                onChange={handleFilesSelected}
                style={{ display: "none" }}
              />

              {/* Attached files chips */}
              {replyAttachments.length > 0 && (
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                  {replyAttachments.map((f, idx) => (
                    <Chip
                      key={`${f.name}-${idx}`}
                      icon={<FileText size={12} />}
                      label={`${f.name} (${formatSize(f.size)})`}
                      size="small"
                      onDelete={() => handleRemoveAttachment(idx)}
                      deleteIcon={<X size={12} />}
                      sx={{ height: 24, fontSize: "0.65rem", borderRadius: "6px" }}
                    />
                  ))}
                </Box>
              )}

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setReplyOpen(false)}
                  sx={{ textTransform: "none", fontSize: "0.75rem" }}
                >
                  {t("inbox_cancel")}
                </Button>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={handlePickFiles}
                    startIcon={<Paperclip size={14} />}
                    sx={{ textTransform: "none", fontSize: "0.75rem", color: theme.palette.text.secondary }}
                  >
                    {t("inbox_attach") || "Joindre"}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleReply}
                    disabled={sending || !replyBody.trim()}
                    startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <Send size={14} />}
                    sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.75rem" }}
                  >
                    {t("inbox_send")}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Create Organization Dialog */}
      <Dialog open={createOrgOpen} onClose={() => setCreateOrgOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontSize: "1rem" }}>
          Créer une organisation
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="Nom de l'organisation *"
                value={orgForm.org_name}
                onChange={(e) => setOrgForm((p) => ({ ...p, org_name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth size="small" label="Prénom contact"
                value={orgForm.contact_first_name}
                onChange={(e) => setOrgForm((p) => ({ ...p, contact_first_name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth size="small" label="Nom contact"
                value={orgForm.contact_last_name}
                onChange={(e) => setOrgForm((p) => ({ ...p, contact_last_name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={7}>
              <TextField
                fullWidth size="small" label="Email contact"
                value={orgForm.contact_email}
                onChange={(e) => setOrgForm((p) => ({ ...p, contact_email: e.target.value }))}
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                fullWidth size="small" label="Téléphone"
                value={orgForm.contact_phone}
                onChange={(e) => setOrgForm((p) => ({ ...p, contact_phone: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOrgOpen(false)} sx={{ textTransform: "none" }}>
            {t("inbox_cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateOrg}
            disabled={creatingOrg || !orgForm.org_name.trim()}
            startIcon={creatingOrg ? <CircularProgress size={14} color="inherit" /> : <Plus size={14} />}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Créer & Lier
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailThread;
